/** The sign-in providers' names, logos and failure sentences, and the buttons Logg inn shows. */
import { API_BASE } from '@/config';
import { T } from '@/i18n';
import { trackLeaving } from '@/marketing/analytics';
import { type MouseEvent, type ReactNode, useEffect, useState } from 'react';

export const PROVIDER_NAME: Record<string, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
  apple: 'Apple',
};
export const SIGN_IN_FAILED: Record<string, string> = {
  konto: 'Denne adressen kan ikke logge inn slik. Logg inn med e-post og passord.',
  cancelled: 'Innloggingen ble avbrutt. Prøv igjen, eller bruk e-post.',
  unverified: 'Vi fikk ikke bekreftet e-postadressen. Bruk e-post i stedet.',
};

/**
 * What the page says when a sign-in came back with ?innlogging=<reason>. For `konto` the callback
 * also says which provider was tried (`via`), never the address: URLs are kept by replays, history
 * and logs. The sentence names the address by where it came from (replay case L2, 2026-10-13).
 */
export function signInFailedText(failed: string, via: string | null): string {
  const base = T(SIGN_IN_FAILED[failed] ?? 'Innloggingen gikk ikke. Prøv igjen, eller bruk e-post.');
  if (failed !== 'konto' || !via) return base;
  return `${base} ${T('Bruk samme adresse som du valgte hos {name}.', { name: PROVIDER_NAME[via] ?? via })}`;
}

/**
 * One press per visit to the provider. Both lost replays (L1, L2, 2026-10-13) pressed the button
 * twice within a second: the second press starts a second sign-in, whose state cookie replaces the
 * first one's, and the first answer is then refused. After the press the button reads "Åpner Google …"
 * and ignores further presses, until the page regains focus without having navigated (the person
 * came back, or the provider never opened), when it is a button again.
 */
export function useOnePress(page: 'login' | 'signup') {
  const [opening, setOpening] = useState<string | null>(null);
  useEffect(() => {
    if (!opening) return;
    const back = () => setOpening(null);
    window.addEventListener('focus', back);
    window.addEventListener('pageshow', back);
    return () => {
      window.removeEventListener('focus', back);
      window.removeEventListener('pageshow', back);
    };
  }, [opening]);
  const press = (p: string, e: MouseEvent) => {
    if (opening) {
      e.preventDefault();
      return;
    }
    setOpening(p);
    trackLeaving('signin_method_chosen', { method: p, page });
  };
  return { opening, press };
}

/** "Fortsett med Google/Microsoft/Apple": straight into the demo, no mail; from the login page into their own company (services/oauth.js). */
export function SignInWith({
  providers,
  failed,
  via = null,
  login = false,
  forCompany = false,
}: {
  providers: string[];
  failed: string | null;
  via?: string | null;
  login?: boolean;
  /** the own-company sign-up (registrer?next=oppsett, #191): an account and «Opprett bedrift», no demo */
  forCompany?: boolean;
}) {
  const { opening, press } = useOnePress(login ? 'login' : 'signup');
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {failed && <div className="al dgr">{signInFailedText(failed, via)}</div>}
      {providers.map((p) => {
        const name = PROVIDER_NAME[p] ?? p;
        return (
          <a
            key={p}
            className="btn auth-prov"
            href={`${API_BASE}/account/oauth/${p}${login ? '?mode=login' : forCompany ? '?mode=oppsett' : ''}`}
            data-testid={`sign-in-${p}`}
            aria-disabled={opening != null || undefined}
            onClick={(e) => press(p, e)}
          >
            {LOGO[p]}
            {opening === p ? T('Åpner {name} …', { name }) : T('Fortsett med {name}', { name })}
          </a>
        );
      })}
      <p className="auth-or">{T('eller')}</p>
    </div>
  );
}

export const LOGO: Record<string, ReactNode> = {
  google: (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  ),
  microsoft: (
    <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
      <path fill="#f35325" d="M1 1h10v10H1z" />
      <path fill="#81bc06" d="M12 1h10v10H12z" />
      <path fill="#05a6f0" d="M1 12h10v10H1z" />
      <path fill="#ffba08" d="M12 12h10v10H12z" />
    </svg>
  ),
};

const AppleLogo = () => (
  <svg width="16" height="19" viewBox="0 0 384 470" aria-hidden="true">
    <path
      fill="currentColor"
      d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
    />
  </svg>
);
LOGO.apple = <AppleLogo />;
